import { createResource } from './resource.js';
import { createLifecycle } from './lifecycle.js';
export * from './resource.js';
export * from './lifecycle.js';
export class ApiError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = 'ApiError';
    }
}
export const PERMISSIONS = [
    'gadgets:view', 'gadgets:edit', 'repairs:log', 'users:manage', 'data:export', 'audit:view',
];
export function createApiClient(opts) {
    const doFetch = opts.fetch ?? globalThis.fetch;
    const base = opts.baseUrl.replace(/\/$/, '');
    async function request(path, init = {}) {
        const token = opts.getToken?.() ?? null;
        const res = await doFetch(`${base}/v1${path}`, {
            ...init,
            headers: {
                // Only on requests that carry a body. An unnecessary content-type on a GET turns a
                // simple cross-origin request into a preflighted one for no benefit.
                ...(init.body ? { 'content-type': 'application/json' } : {}),
                ...(token ? { authorization: `Bearer ${token}` } : {}),
                ...init.headers,
            },
        });
        const body = (await res.json().catch(() => ({})));
        if (!res.ok) {
            const err = (body['error'] ?? {});
            throw new ApiError(res.status, err.code ?? 'unknown', err.message ?? res.statusText);
        }
        return body;
    }
    const transport = { request };
    const gadgets = createResource(transport, {
        path: '/gadgets',
        verbs: ['list', 'get', 'create', 'update', 'remove'],
    });
    const repairs = createResource(transport, {
        path: '/repairs',
        verbs: ['list', 'create'],
    });
    const members = createResource(transport, {
        path: '/members',
        verbs: ['list'],
    });
    const audit = createResource(transport, {
        path: '/audit',
        verbs: ['list'],
    });
    return {
        gadgets,
        repairs,
        members,
        audit,
        lifecycle: createLifecycle(transport),
        uploadDocument: async (gadgetId, file, input) => {
            const ticket = await request('/gadgets/' + encodeURIComponent(gadgetId) + '/documents', { method: 'POST', body: JSON.stringify(input) });
            const put = await doFetch(ticket.uploadUrl, {
                method: 'PUT', headers: { 'content-type': input.contentType }, body: file,
            });
            if (!put.ok)
                throw new ApiError(put.status, 'upload_failed', 'the file could not be stored');
            await request('/documents/' + ticket.documentId + '/complete', { method: 'POST', body: '{}' });
            return ticket.documentId;
        },
        importCsv: async (csv) => {
            const token = opts.getToken?.() ?? null;
            const res = await doFetch(base + '/v1/gadgets/import', {
                method: 'POST',
                headers: { 'content-type': 'text/csv', ...(token ? { authorization: 'Bearer ' + token } : {}) },
                body: csv,
            });
            const body = await res.json().catch(() => ({}));
            if (res.ok || res.status === 422)
                return { imported: body.imported ?? 0, failures: body.failures ?? [] };
            throw new ApiError(res.status, body.error?.code ?? 'import_failed', body.error?.message ?? res.statusText);
        },
        health: () => request('/health'),
        me: () => request('/me'),
        listMembers: () => request('/members'),
        listGadgets: (q) => request('/gadgets' + (q?.search ? '?search=' + encodeURIComponent(q.search) : '')),
        listAudit: () => request('/audit'),
        getDashboard: () => request('/dashboard'),
        getMyDevices: () => request('/dashboard/mine'),
        /** CSV, not JSON — so it bypasses request() and reads the body as text. */
        exportCsv: async (what) => {
            const token = opts.getToken?.() ?? null;
            const res = await doFetch(base + '/v1/export/' + what + '.csv', {
                headers: token ? { authorization: 'Bearer ' + token } : {},
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new ApiError(res.status, 'export_failed', body.error?.message ?? res.statusText);
            }
            const disposition = res.headers.get('content-disposition') ?? '';
            const named = /filename="([^"]+)"/.exec(disposition);
            return { filename: named?.[1] ?? what + '.csv', csv: await res.text() };
        },
        getTimeline: (gadgetId) => request(`/gadgets/${gadgetId}/timeline`),
        getDocumentUrl: (documentId) => request(`/documents/${documentId}/url`),
        setPermission: (membershipId, permission, granted) => request('/members/permissions', {
            method: 'POST',
            body: JSON.stringify({ membershipId, permission, granted }),
        }),
    };
}
//# sourceMappingURL=index.js.map